'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @a11y-contract Notice (client feedback — collapsed Banner/Alert/Callout)
 *
 * Role:           role="alert" on the root when intent="danger" or announceOn="always".
 *                 role="status" on the root for polite "transition".
 *                 Polite "mount" puts the role on a visually hidden mirror instead.
 *                 All are announced without moving focus (see Live region).
 * RSC:            No — 'use client' (dismissible state, ESC handler, focus restore).
 * Focus:          On dismiss, focus is returned to the element that was focused
 *                 before the Notice appeared (stored in a ref at mount).
 *                 <Notice.Action> renders a focusable button inside the notice.
 *                 Dismiss button: last focusable element in the notice.
 * Keyboard:       ESC dismisses only when dismissible=true.
 *                 Tab traverses action button → dismiss button naturally.
 * Forced-colors:  Intent conveyed by icon + text, not colour alone (Pillar 14).
 *                 Background is suppressed; border drawn in ButtonText.
 * RTL:            Leading icon + content + dismiss button use flex + logical gap.
 * Reduced-motion: Entry animation respects prefers-reduced-motion.
 * Touch target:   Dismiss IconButton ≥ 44×44.
 * Labels:         role=alert/status provides live-region announcement.
 *                 Dismiss button: aria-label="Dismiss notification".
 *                 Intent icon: aria-hidden (intent announced via title text).
 * Live region:    Title and body are visible on the first paint in every mode.
 *                 A region only announces changes made after AT registered it,
 *                 so each mode picks a structure that works with that:
 *                   assertive    → root is role="alert" holding its text at
 *                   (danger,       insertion; alert is announced on insertion.
 *                   "always")
 *                   "transition" → root is role="status" from the first render:
 *                                  text present at mount is not announced, later
 *                                  changes are (default for non-danger intents).
 *                   "mount"      → root carries no live role. A VisuallyHidden
 *                   (polite)       role="status" mirror mounts empty, receives a
 *                                  plain-text copy of the title/body once it is
 *                                  registered (and again when the title changes),
 *                                  then empties so browse mode does not keep a
 *                                  second copy. Interactive children are never
 *                                  mirrored.
 * PRM (Pillar 12):padding/gap use --space-* tokens.
 */
import { useEffect, useRef, useState } from 'react';
import { X, Info, CheckCircle, AlertTriangle, AlertCircle } from 'lucide-react';
import { VisuallyHidden } from '../../../VisuallyHidden/VisuallyHidden';
import styles from './Notice.module.css';
const INTENT_ICON = {
    info: _jsx(Info, { size: 16, "aria-hidden": "true" }),
    success: _jsx(CheckCircle, { size: 16, "aria-hidden": "true" }),
    warning: _jsx(AlertTriangle, { size: 16, "aria-hidden": "true" }),
    danger: _jsx(AlertCircle, { size: 16, "aria-hidden": "true" }),
};
// Text inserted too soon after its region is created is dropped (Safari +
// VoiceOver most visibly); react-aria's LiveAnnouncer waits 100ms for this.
const REGION_REGISTRATION_DELAY_MS = 100;
// Long enough for a polite announcement to be queued and spoken; react-aria
// clears its announcer after the same 7s.
const MIRROR_CLEAR_DELAY_MS = 7000;
function NoticeAction({ children, onClick }) {
    return (_jsx("button", { type: "button", className: styles.action, onClick: onClick, children: children }));
}
export function Notice({ intent = 'info', placement = 'inline', title, children, dismissible, onDismiss, announceOn, className, }) {
    const isDismissible = dismissible ?? intent !== 'danger';
    const effectiveAnnounceOn = announceOn ?? (intent === 'danger' ? 'mount' : 'transition');
    const assertive = intent === 'danger' || effectiveAnnounceOn === 'always';
    const mirrored = !assertive && effectiveAnnounceOn === 'mount';
    const [visible, setVisible] = useState(true);
    const [mirrorText, setMirrorText] = useState('');
    const previousFocusRef = useRef(null);
    const bodyRef = useRef(null);
    useEffect(() => {
        previousFocusRef.current = document.activeElement;
    }, []);
    useEffect(() => {
        if (!mirrored || !visible)
            return undefined;
        let clearId;
        const fillId = setTimeout(() => {
            setMirrorText(bodyRef.current?.textContent ?? title);
            clearId = setTimeout(() => setMirrorText(''), MIRROR_CLEAR_DELAY_MS);
        }, REGION_REGISTRATION_DELAY_MS);
        return () => {
            clearTimeout(fillId);
            if (clearId !== undefined)
                clearTimeout(clearId);
            setMirrorText('');
        };
    }, [mirrored, visible, title]);
    function dismiss() {
        if (!isDismissible)
            return;
        setVisible(false);
        onDismiss?.();
        if (previousFocusRef.current instanceof HTMLElement) {
            previousFocusRef.current.focus();
        }
    }
    function handleKeyDown(e) {
        if (e.key === 'Escape' && isDismissible) {
            e.stopPropagation();
            dismiss();
        }
    }
    if (!visible)
        return null;
    const rootRole = assertive ? 'alert' : mirrored ? undefined : 'status';
    const cls = [
        styles.root,
        styles[`intent--${intent}`],
        styles[`placement--${placement}`],
        className,
    ]
        .filter(Boolean)
        .join(' ');
    return (_jsxs("div", { role: rootRole, "aria-live": rootRole === undefined ? undefined : assertive ? 'assertive' : 'polite', "aria-atomic": rootRole === undefined ? undefined : true, className: cls, onKeyDown: handleKeyDown, children: [_jsx("span", { className: styles.icon, "aria-hidden": "true", children: INTENT_ICON[intent] }), _jsxs("div", { className: styles.body, ref: bodyRef, children: [_jsx("span", { className: styles.title, children: title }), children && _jsx("div", { className: styles.content, children: children })] }), isDismissible && (_jsx("button", { type: "button", className: styles.dismiss, onClick: dismiss, "aria-label": "Dismiss notification", children: _jsx(X, { size: 16, "aria-hidden": "true" }) })), mirrored && (_jsx(VisuallyHidden, { role: "status", "aria-live": "polite", "aria-atomic": "true", children: mirrorText }))] }));
}
Notice.Action = NoticeAction;
//# sourceMappingURL=Notice.js.map